import {
    ArrayOrSingle,
    enumIsEnumValue,
    HideableValue,
    hideableValueIsHidden,
    IsExactValueType,
    iterableMapToArray,
    objectCreateFromEntries,
    objectGetEntries,
    objectGetKeys,
    objectPick,
    objectRemoveEntries,
    OmitByValue, OverridableValue,
    RichTextContent,
    Values
} from "@wix/devzai-utils-common";
import type {DoppeActionPriceData, DoppeListActionSettings} from "../types";
import type {DoppeActionCTATypeLinkSource} from "../types/doppe-action-cta-button";
import type {DoppeEntityBindingStateInfo} from '../bymo-page-props-traversal/bymo-page-props-traversal';
import type {WixImageResource, WixMediaResource, WixVideoResource} from "@wix/devzai-common-wix";
import type {BymoPageMedia} from "../bymo-pages/bymo-page-media";
import type {DoppeDtoContactMethod} from "../types/doppe-dto-contact-method";
import type {DoppeActionFullName} from "../types/doppe-action-full-name";
import {DoppeMenuTitleSource, DoppePageProfileNameSource} from "../bymo-pages";
import type {
    BymoPagePropsContentSettingTypeValueMapFor,
    BymoPagePropsContentTypeMap,
    BymoPagePropsDefaultContentTypes,
} from '../bymo-page-props-type-mapping/bymo-page-props-type-mapping';
import type {BymoPagePropsAiStringKey} from './bymo-page-props-ai-strings';

export const BymoPagePropsContentSettingSpecialSemanticMeaning = {
    Unspecified: 'Unspecified',
} as const;

export type BymoPagePropsContentSettingSpecialSemanticMeaning = Values<typeof BymoPagePropsContentSettingSpecialSemanticMeaning>;

export const BymoPagePropsContentSettingContextDependentSemanticMeaning = {
    /** The primary title or name of the entity. For a product action, this is the product name. For an event action, this is the event title. For a real estate property action, this is the property name. For a link action, this is the link title. */
    EntityTitle: 'EntityTitle',
    /** The publication or creation date of the entity. For a blog post action, this is when the post was published. For an event action, this is the event start date. For a product action, this might be when the product was launched. */
    EntityPublishDate: 'EntityPublishDate',
    /** The descriptive text or content that explains what the entity is about. For a product action, this describes the product features and details. For an event action, this explains what the event is about. Can be plain text or rich formatted content. */
    EntityDescription: 'EntityDescription',
    /** The company or organization name associated with the entity. For a product action, this is the manufacturer or brand. For an event action, this is the event organizer. For a service action, this is the service provider company name. */
    EntityCompany: 'EntityCompany',
    /** The various ways to contact the entity or its provider. For a business action, these are the business contact methods (phone, email, social links). For a person action, these are the person's contact details. Typically includes phone numbers, email addresses, and social media links. */
    EntityContactMethods: 'EntityContactMethods',
    /** A small label or badge text that highlights special status or promotions. For a product action, this could be "SALE", "NEW", "LIMITED EDITION". For an event action, this could be "SOLD OUT", "VIP", "EARLY BIRD". Usually displays prominently on the entity card. */
    EntityRibbon: 'EntityRibbon',
    /** The physical location or address associated with the entity. For a business action, this is the business address. For an event action, this is where the event takes place. For a real estate property action, this is the property address. */
    EntityAddress: 'EntityAddress',
    /** A promotional or discount code that can be applied to the entity. For a product action, this is a coupon code for purchasing the product. For an event action, this is a discount code for event tickets. For a link action pointing to an external site, this is a code that can be used on that site. */
    EntityCouponCode: 'EntityCouponCode',
    /** A URL that links to a dedicated page or external resource for the entity. For a product action, this is the link to the product page on the seller's website. For an event action, this is the link to detailed event information or registration. For a link action, this is the primary destination URL. */
    EntityPageUrl: 'EntityPageUrl',
    /** Visual media (images or videos) associated with the entity. For a product action, these are product photos or demo videos. For an event action, these are event photos, venue pictures, or promotional videos. For a person action, this is the profile photo or videos. Can be single or multiple media items. */
    EntityMedia: 'EntityMedia',
    /** Information about when the entity is scheduled to occur or be available. For an event action, this includes date, time, and duration of the event. For a course action, this includes class schedule and timings. For a limited-time offer action, this is when the offer is valid. */
    EntityScheduleInfo: 'EntityScheduleInfo',
    /** Details about who will be participating in or attending the entity. For an event action, this lists speakers, performers, or guest attendees. For a workshop action, this mentions the instructors. For a meeting action, this lists the participants. */
    EntityParticipantsInfo: 'EntityParticipantsInfo',
    /** How long the entity lasts or takes. For an event action, this is the event duration (e.g., "2 hours", "3 days"). For a course action, this is the course length. For a video action, this is the video runtime. For a service action, this is the service duration. */
    EntityDuration: 'EntityDuration',
    /** The text displayed on the primary action button for the entity. For a product action, this might be "Buy Now", "Add to Cart", "View Product". For an event action, this might be "Register", "Buy Tickets", "RSVP". For a link action, this might be "Visit Website", "Learn More", "Get Started". */
    EntityActionButtonText: 'EntityActionButtonText',
} as const;

export type BymoPagePropsContentSettingContextDependentSemanticMeaning = Values<typeof BymoPagePropsContentSettingContextDependentSemanticMeaning>;

export const BymoPagePropsContentSettingContextIndependentSemanticMeaning = {
    /** A person's professional title or role in their organization. Examples: "Software Engineer", "CEO", "Marketing Director", "Product Designer", "Sales Manager". Used in person profile actions, team member displays, or contact cards. */
    PersonJobTitle: 'PersonJobTitle',
    /** A person's full name or display name. This could be first and last name combined, or a complete name structure. Used in person profile actions, author credits, team member cards, or anywhere a person's identity is displayed. */
    PersonName: 'PersonName',
    /** The profile name or identity that represents the page owner. This could be a person's name (e.g., "John Smith"), a brand name (e.g., "JUTLAND BAGS"), a business name (e.g., "Joe's Coffee Shop"), or any other identity. When localizing, person names should use culturally appropriate names preserving gender, while brand/business names should be adapted to sound natural in the target language. */
    ProfileIdentity: 'ProfileIdentity',
    /** The main heading text displayed prominently on a page. This is typically the largest, most visible text that introduces the page content. For a link-in-bio page, this might be the user's name or tagline. For an instant page, this is the page's primary headline. Different from page title which is for SEO/browser tabs. */
    PageHeading: 'PageHeading',

    PageSubheading: 'PageSubheading',
    /** The page title used for SEO, browser tabs, and metadata. This is what appears in the browser tab, search engine results, and when sharing the page on social media. Different from page heading which is displayed on the page itself. Should be concise and descriptive for search engines. */
    PageTitle: 'PageTitle',
    /** Hero or banner media displayed prominently at the top of a page. These are the large, eye-catching images or videos that create the first visual impression of the page. For a link-in-bio page, this could be a background image. For an instant page, this is the hero section media. Can be single or multiple media items. */
    PageHeroMedia: 'PageHeroMedia',
    /** A cover or thumbnail image that represents the page in previews and listings. This is the image shown when the page is shared on social media, displayed in page galleries, or used as a preview. Different from hero media which is displayed on the page itself. Typically a single image optimized for sharing. */
    PageCoverMedia: 'PageCoverMedia',
} as const;

export type BymoPagePropsContentSettingContextIndependentSemanticMeaning = Values<typeof BymoPagePropsContentSettingContextIndependentSemanticMeaning>;


export const BymoPagePropsContentSettingSemanticMeaning = {
    ...BymoPagePropsContentSettingContextDependentSemanticMeaning,
    ...BymoPagePropsContentSettingContextIndependentSemanticMeaning
} as const;

export type BymoPagePropsContentSettingSemanticMeaning = Values<typeof BymoPagePropsContentSettingSemanticMeaning>;

export const BymoPagePropsContentSettingType = {
    Text: 'Text',
    Number: 'Number',
    ListOfTexts: 'ListOfTexts',
    PriceData: 'PriceData',
    RichText: 'RichText',
    Boolean: 'Boolean',
    Url: 'Url',
    Phone: 'Phone',
    /**
     * Link source for CTA buttons - can be a URL string, page reference, or entity reference.
     */
    LinkSource: 'LinkSource',
    MultipleMedia: 'MultipleMedia',
    MultipleImages: 'MultipleImages',
    PersonFullName: 'PersonFullName',
    ContactMethods: 'ContactMethods',
    // Supports also render media in a player.
    MultipleBymoPageMedia: 'MultipleBymoPageMedia',
    SingleMedia: 'SingleMedia',
    SingleRequiredMedia: 'SingleRequiredMedia',
    SingleImage: 'SingleImage',
    SingleVideo: 'SingleVideo',
    Rating: 'Rating',
    /**
     * ISO 8601 date-time string from Date.toJSON(), e.g., "2024-03-05T00:00:00.000Z".
     */
    DateTimeString: 'DateTimeString',
    ProfileNameSource: 'ProfileNameSource',
    NavigationMenuTitleSource: 'NavigationMenuTitleSource',
} as const;

export type BymoPagePropsContentSettingType = Values<typeof BymoPagePropsContentSettingType>;

const SemanticMeaningAllowedSettingTypes = {
    [BymoPagePropsContentSettingSemanticMeaning.EntityTitle]: {
        [BymoPagePropsContentSettingType.Text]: true,
        [BymoPagePropsContentSettingType.RichText]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityDescription]: {
        [BymoPagePropsContentSettingType.Text]: true,
        [BymoPagePropsContentSettingType.RichText]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.PersonJobTitle]: {
        [BymoPagePropsContentSettingType.Text]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityCompany]: {
        [BymoPagePropsContentSettingType.Text]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityScheduleInfo]: {
        [BymoPagePropsContentSettingType.Text]: true,
        [BymoPagePropsContentSettingType.RichText]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityParticipantsInfo]: {
        [BymoPagePropsContentSettingType.Text]: true,
        [BymoPagePropsContentSettingType.RichText]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityDuration]: {
        [BymoPagePropsContentSettingType.Text]: true,
        [BymoPagePropsContentSettingType.RichText]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityRibbon]: {
        [BymoPagePropsContentSettingType.Text]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityPublishDate]: {
        [BymoPagePropsContentSettingType.Text]: true,
        [BymoPagePropsContentSettingType.DateTimeString]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityActionButtonText]: {
        [BymoPagePropsContentSettingType.Text]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityAddress]: {
        [BymoPagePropsContentSettingType.Text]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityCouponCode]: {
        [BymoPagePropsContentSettingType.Text]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityPageUrl]: {
        [BymoPagePropsContentSettingType.Url]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityContactMethods]: {
        [BymoPagePropsContentSettingType.ContactMethods]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.PersonName]: {
        [BymoPagePropsContentSettingType.PersonFullName]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.ProfileIdentity]: {
        [BymoPagePropsContentSettingType.ProfileNameSource]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.EntityMedia]: {
        [BymoPagePropsContentSettingType.MultipleMedia]: true,
        [BymoPagePropsContentSettingType.MultipleImages]: true,
        [BymoPagePropsContentSettingType.MultipleBymoPageMedia]: true,
        [BymoPagePropsContentSettingType.SingleMedia]: true,
        [BymoPagePropsContentSettingType.SingleRequiredMedia]: true,
        [BymoPagePropsContentSettingType.SingleImage]: true,
        [BymoPagePropsContentSettingType.SingleVideo]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.PageHeading]: {
        [BymoPagePropsContentSettingType.Text]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.PageSubheading]: {
        [BymoPagePropsContentSettingType.Text]: true,
        [BymoPagePropsContentSettingType.RichText]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.PageTitle]: {
        [BymoPagePropsContentSettingType.Text]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.PageHeroMedia]: {
        [BymoPagePropsContentSettingType.MultipleMedia]: true,
        [BymoPagePropsContentSettingType.MultipleImages]: true,
        [BymoPagePropsContentSettingType.MultipleBymoPageMedia]: true,
        [BymoPagePropsContentSettingType.SingleMedia]: true,
    },
    [BymoPagePropsContentSettingSemanticMeaning.PageCoverMedia]: {
        [BymoPagePropsContentSettingType.SingleMedia]: true,
        [BymoPagePropsContentSettingType.SingleImage]: true,
    },
} satisfies Record<BymoPagePropsContentSettingSemanticMeaning, Partial<Record<BymoPagePropsContentSettingType, true>>>;

type SemanticMeaningAllowedSettingTypes = typeof SemanticMeaningAllowedSettingTypes;

export namespace BymoPagePropsContentSettingSemanticMeaning {
    export type ResolveAllowedForSettingType<T extends BymoPagePropsContentSettingType> = ({
        [K in keyof SemanticMeaningAllowedSettingTypes]: T extends keyof SemanticMeaningAllowedSettingTypes[K] ? K : never;
    }[keyof SemanticMeaningAllowedSettingTypes]) | BymoPagePropsContentSettingSpecialSemanticMeaning
}


export const BymoPagePropsSpecialContentSettingMetadata = {
    /** 
     * Temporary marker for fields where it's undecided whether they are content or technical fields.
     * Use this when you need more analysis or discussion to determine the field's classification.
     * The field's TypeScript type must already be supported by BymoPagePropsValueSupportedMetadata.
     * Eventually should be replaced with either a proper content setting metadata or marked as Ignored.
     */
    Unknown: 'Unknown',
    /** 
     * Permanent marker for technical fields that should never be considered content.
     * Use this for fields that are technical settings, bindings, display preferences, or configuration.
     * Examples: productProviderId (binding reference), showButton (display toggle), layoutConfig (technical setting).
     * These fields are excluded from all content operations like AI generation, translation, and semantic search.
     */
    Ignored: 'Ignored',
    /** 
     * Temporary marker for fields that appear to be content but whose TypeScript type is not yet supported.
     * Use this when a field looks like content but its type hasn't been added to BymoPagePropsValueSupportedMetadata yet.
     * Once type support is added to BymoPagePropsValueSupportedMetadata, this should be replaced with either 
     * proper content setting metadata or marked as Ignored after determining if it's truly content or technical.
     */
    ToBeConsidered: 'ToBeConsidered',
} as const;

export type BymoPagePropsSpecialContentSettingMetadata = Values<typeof BymoPagePropsSpecialContentSettingMetadata>;

type BymoPagePropsPickPotentialContentSettings<SETTINGS extends {}> = Pick<SETTINGS, keyof OmitByValue<{
    [K in keyof SETTINGS]: BymoPagePropsValueSupportedMetadata<SETTINGS[K]>
}, never>>

export type BymoPagePropsPotentialContentSettingsNames<SETTINGS extends {}> = keyof BymoPagePropsPickPotentialContentSettings<SETTINGS>

export type BymoPagePropsContentSettingsMetadata<
    SETTINGS extends {},
    POTENTIAL_CONTENT_SETTINGS = BymoPagePropsPickPotentialContentSettings<SETTINGS>
> = {
    [K in keyof POTENTIAL_CONTENT_SETTINGS]: BymoPagePropsValueSupportedMetadata<POTENTIAL_CONTENT_SETTINGS[K]> | Exclude<BymoPagePropsSpecialContentSettingMetadata, typeof BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered>;
} & {
    [K in Exclude<keyof SETTINGS, keyof POTENTIAL_CONTENT_SETTINGS>]?: typeof BymoPagePropsSpecialContentSettingMetadata.ToBeConsidered;
}

export namespace BymoPagePropsContentSettingsMetadata {

    /**
     * @example
     * ```
     * // Will extract the fields that marked as ignored in the DoppeActionContentSectionsGeneralSettingsMetadata
     * BymoPagePropsContentSettingsMetadata.ExtractIgnoredKeys<typeof DoppeActionContentSectionsGeneralSettingsMetadata>
     * ```
     */
    export type ExtractIgnoredKeys<TMetadata> = {
        [K in keyof TMetadata]: TMetadata[K] extends typeof BymoPagePropsSpecialContentSettingMetadata.Ignored ? K : never
    }[keyof TMetadata];
}

/**
 * Maps a value type to the corresponding content setting metadata type.
 * Used to enforce that metadata types match the value types they describe.
 * 
 * @see BymoPagePropsContentSettingTypeValueMap for the inverse mapping (content setting type → value type)
 */
export type BymoPagePropsValueSupportedMetadata<T, V = OverridableValue.ResolveValueType<HideableValue.ResolveValueType<T>>> =
    IsExactValueType<RichTextContent, V> extends true ? BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.RichText>:
    IsExactValueType<DoppePageProfileNameSource, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.ProfileNameSource>) :
    IsExactValueType<DoppeMenuTitleSource, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.NavigationMenuTitleSource>) :
    // LinkSource must be checked before string, since DoppeLinkSpec includes string as a variant
    IsExactValueType<DoppeActionCTATypeLinkSource, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.LinkSource>) :
    IsExactValueType<string, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.Text | typeof BymoPagePropsContentSettingType.Url | typeof BymoPagePropsContentSettingType.Phone | typeof BymoPagePropsContentSettingType.DateTimeString>) :
    IsExactValueType<string[], V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.ListOfTexts>) :
    IsExactValueType<DoppeActionFullName, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.PersonFullName>) :
    IsExactValueType<DoppeDtoContactMethod[], V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.ContactMethods>) :
    IsExactValueType<WixImageResource[] | null, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.MultipleImages>) :
    IsExactValueType<WixImageResource[], V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.MultipleImages>) :
    IsExactValueType<ArrayOrSingle<WixMediaResource> | null, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.MultipleMedia>) :
    IsExactValueType<BymoPageMedia[] | null, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.MultipleBymoPageMedia>) :
    IsExactValueType<WixMediaResource[] | null, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.MultipleMedia>) :
    IsExactValueType<WixMediaResource[], V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.MultipleMedia>) :
    IsExactValueType<WixImageResource | null, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.SingleImage>) :
    IsExactValueType<WixVideoResource | null, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.SingleVideo>) :
    IsExactValueType<WixMediaResource | null, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.SingleMedia>) :
    IsExactValueType<WixMediaResource, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.SingleRequiredMedia>) :
    IsExactValueType<1 | 2 | 3 | 4 | 5, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.Rating>) :
    IsExactValueType<DoppeActionPriceData, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.PriceData>) :
    IsExactValueType<number, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.Number>) :
    IsExactValueType<boolean, V> extends true ? (BymoPagePropsContentSettingMetadata<typeof BymoPagePropsContentSettingType.Boolean>) :
    never;

/**
 * Maps content setting types to their corresponding value types.
 * This is the inverse of BymoPagePropsValueSupportedMetadata.
 * 
 * Used to provide type-safe field values based on content setting type.
 * 
 * @see BymoPagePropsValueSupportedMetadata for the inverse mapping (value type → metadata type)
 */
export type BymoPagePropsContentSettingTypeValueMap = {
    [BymoPagePropsContentSettingType.RichText]: RichTextContent;
    [BymoPagePropsContentSettingType.ProfileNameSource]: DoppePageProfileNameSource;
    [BymoPagePropsContentSettingType.NavigationMenuTitleSource]: DoppeMenuTitleSource;
    [BymoPagePropsContentSettingType.Text]: string;
    [BymoPagePropsContentSettingType.Url]: string;
    [BymoPagePropsContentSettingType.Phone]: string;
    [BymoPagePropsContentSettingType.LinkSource]: DoppeActionCTATypeLinkSource;
    [BymoPagePropsContentSettingType.ListOfTexts]: string[];
    [BymoPagePropsContentSettingType.PersonFullName]: DoppeActionFullName;
    [BymoPagePropsContentSettingType.ContactMethods]: DoppeDtoContactMethod[];
    [BymoPagePropsContentSettingType.MultipleImages]: WixImageResource[] | null;
    [BymoPagePropsContentSettingType.MultipleMedia]: ArrayOrSingle<WixMediaResource> | WixMediaResource[] | null;
    [BymoPagePropsContentSettingType.MultipleBymoPageMedia]: BymoPageMedia[] | null;
    [BymoPagePropsContentSettingType.SingleImage]: WixImageResource | null;
    [BymoPagePropsContentSettingType.SingleVideo]: WixVideoResource | null;
    [BymoPagePropsContentSettingType.SingleMedia]: WixMediaResource | null;
    [BymoPagePropsContentSettingType.SingleRequiredMedia]: WixMediaResource;
    [BymoPagePropsContentSettingType.Rating]: 1 | 2 | 3 | 4 | 5;
    [BymoPagePropsContentSettingType.PriceData]: DoppeActionPriceData;
    [BymoPagePropsContentSettingType.DateTimeString]: string;
    [BymoPagePropsContentSettingType.Number]: number;
    [BymoPagePropsContentSettingType.Boolean]: boolean;
};

// ================================================================================
// Field Location and Container Type Constants
// ================================================================================

/**
 * Fine-grained location of a content field within the BymoPageProps structure.
 * Used for precise identification of where a field appears in the page hierarchy
 * (e.g., distinguishing between action-level vs list-item-level CTA buttons).
 */
export const BymoPagePropsFieldLocation = {
    PageCommonProps: 'PageCommonProps',
    TemplateProps: 'TemplateProps',
    ActionCommonSettings: 'ActionCommonSettings',
    ActionTypeSettings: 'ActionTypeSettings',
    ActionCtaButton: 'ActionCtaButton',
    ActionContentSection: 'ActionContentSection',
    ListItemSettings: 'ListItemSettings',
    ListItemCtaButton: 'ListItemCtaButton',
    ListItemContentSection: 'ListItemContentSection',
    InnerPageSettings: 'InnerPageSettings',
    NavigationMenuSettings: 'NavigationMenuSettings',
    NavigationMenuItem: 'NavigationMenuItem',
    ListItemTemplateCtaButton: 'ListItemTemplateCtaButton',
    ListItemTemplateContentSection: 'ListItemTemplateContentSection',
    ListItemCtaButtonOverride: 'ListItemCtaButtonOverride',
    ListItemContentSectionOverride: 'ListItemContentSectionOverride',
    SocialLinkSettings: 'SocialLinkSettings',
} as const;
export type BymoPagePropsFieldLocation = Values<typeof BymoPagePropsFieldLocation>;

/**
 * Types of container elements within the BymoPageProps structure.
 * Represents high-level structural elements (actions, list items, buttons, etc.)
 * rather than individual content fields.
 */
export const BymoPagePropsContainerType = {
    Action: 'Action',
    ListItem: 'ListItem',
    CtaButton: 'CtaButton',
    ContentSection: 'ContentSection',
    InnerPage: 'InnerPage',
    NavigationMenuItem: 'NavigationMenuItem',
    SocialLink: 'SocialLink',
} as const;
export type BymoPagePropsContainerType = Values<typeof BymoPagePropsContainerType>;

// ================================================================================
// Location Groups for Discrimination
// ================================================================================

/**
 * Locations that are at page level (no action context).
 * Fields at these locations only have context-independent semantic meanings.
 */
export type BymoPagePropsPageLevelLocation =
    | typeof BymoPagePropsFieldLocation.PageCommonProps
    | typeof BymoPagePropsFieldLocation.TemplateProps
    | typeof BymoPagePropsFieldLocation.NavigationMenuSettings
    | typeof BymoPagePropsFieldLocation.NavigationMenuItem
    | typeof BymoPagePropsFieldLocation.InnerPageSettings
    | typeof BymoPagePropsFieldLocation.SocialLinkSettings;

/**
 * Locations that are at action level (within an action context).
 * Fields at these locations may have context-dependent semantic meanings
 * that require actionTypeId for interpretation.
 */
export type BymoPagePropsActionLevelLocation = Exclude<BymoPagePropsFieldLocation, BymoPagePropsPageLevelLocation>;

// ================================================================================
// BymoPagePropsTypedFieldValue - Self-Descriptive Field Value
// ================================================================================

/**
 * A discriminated union representing a self-descriptive content field value.
 * Contains all information needed to perform operations on a field without 
 * requiring additional context.
 * 
 * Generic over `TMap` (content type map) - supports different type configurations (runtime, AI spec).
 * 
 * Discriminated by `location`:
 * - `PageLevel`: Fields at page level (no action context)
 * - `ActionLevel`: Fields within an action (includes `actionTypeId` for semantic interpretation)
 * 
 * Note: Value wrapping (e.g., with AiGeneratedValue) is handled at the entity/field level,
 * not in the traversal system. The traversal system works with base content types.
 * 
 * @example
 * ```typescript
 * // Default usage (runtime types)
 * function processField(field: BymoPagePropsTypedFieldValue) {
 *     if (field.contentSettingType === BymoPagePropsContentSettingType.SingleMedia) {
 *         // field.value is WixMediaResource | null
 *     }
 * }
 * 
 * // With AI content types
 * function processAiField(field: BymoPagePropsTypedFieldValue<AiContentTypes>) {
 *     if (field.contentSettingType === BymoPagePropsContentSettingType.SingleMedia) {
 *         // field.value is AiMediaSpec | null
 *     }
 * }
 * ```
 */
export type BymoPagePropsTypedFieldValue<
    TMap extends BymoPagePropsContentTypeMap = BymoPagePropsDefaultContentTypes
> =
    | BymoPagePropsTypedFieldValue.PageLevel<TMap>
    | BymoPagePropsTypedFieldValue.ActionLevel<TMap>;

export namespace BymoPagePropsTypedFieldValue {
    /**
     * Base fields shared by all variants.
     * Generic over TMap to use the correct value types.
     */
    interface Base<
        K extends BymoPagePropsContentSettingType,
        TMap extends BymoPagePropsContentTypeMap
    > {
        /** The content setting type (Text, RichText, SingleMedia, etc.) */
        contentSettingType: K;
        /** The property key/name of the field */
        fieldName: string;
        /** The semantic meaning of the field (EntityTitle, PageHeading, etc.) */
        semanticMeaning: BymoPagePropsContentSettingSemanticMeaning.ResolveAllowedForSettingType<K>;
        /** Whether this field is marked as deprecated */
        isDeprecated: boolean;
        /** The unwrapped value (extracted from hideableValue) */
        value: BymoPagePropsContentSettingTypeValueMapFor<TMap>[K];
        /** The original field value which may be a HideableValue wrapper */
        hideableValue: HideableValue<BymoPagePropsContentSettingTypeValueMapFor<TMap>[K]>;
        /** Whether the field is hidden */
        isHidden: boolean;
        /** The entity binding state of this field (None, Bound, or Overridden) */
        bindingState: DoppeEntityBindingStateInfo;
    }

    /**
     * Page-level fields - no action context.
     * Only context-independent semantic meanings are valid.
     */
    export type PageLevel<
        TMap extends BymoPagePropsContentTypeMap = BymoPagePropsDefaultContentTypes
    > = {
        [K in BymoPagePropsContentSettingType]: Base<K, TMap> & {
            /** Where in the page hierarchy this field is located */
            location: BymoPagePropsPageLevelLocation;
        }
    }[BymoPagePropsContentSettingType];

    /**
     * Action-level fields - within an action context.
     * Includes actionTypeId for interpreting context-dependent semantic meanings.
     */
    export type ActionLevel<
        TMap extends BymoPagePropsContentTypeMap = BymoPagePropsDefaultContentTypes
    > = {
        [K in BymoPagePropsContentSettingType]: Base<K, TMap> & {
            /** Where in the page hierarchy this field is located */
            location: BymoPagePropsActionLevelLocation;
            /** The action type ID (required for interpreting context-dependent semantic meanings) */
            actionTypeId: string;
        }
    }[BymoPagePropsContentSettingType];
}

// Set of page-level locations for runtime checking
const PAGE_LEVEL_LOCATIONS: Set<BymoPagePropsFieldLocation> = new Set([
    BymoPagePropsFieldLocation.PageCommonProps,
    BymoPagePropsFieldLocation.TemplateProps,
    BymoPagePropsFieldLocation.NavigationMenuSettings,
    BymoPagePropsFieldLocation.NavigationMenuItem,
    BymoPagePropsFieldLocation.InnerPageSettings,
    BymoPagePropsFieldLocation.SocialLinkSettings,
]);

/**
 * Type guard to check if a field location is at page level.
 */
export function bymoPagePropsFieldLocationIsPageLevel(
    location: BymoPagePropsFieldLocation
): location is BymoPagePropsPageLevelLocation {
    return PAGE_LEVEL_LOCATIONS.has(location);
}

/**
 * Type guard to check if a typed field value is at page level.
 * Page-level fields have no action context.
 */
export function bymoPagePropsTypedFieldValueIsPageLevel<
    TMap extends BymoPagePropsContentTypeMap
>(
    value: BymoPagePropsTypedFieldValue<TMap>
): value is BymoPagePropsTypedFieldValue.PageLevel<TMap> {
    return bymoPagePropsFieldLocationIsPageLevel(value.location);
}

/**
 * Type guard to check if a typed field value is at action level.
 * Action-level fields have an actionTypeId for interpreting context-dependent semantic meanings.
 * 
 * @example
 * ```typescript
 * function processField(field: BymoPagePropsTypedFieldValue) {
 *     if (bymoPagePropsTypedFieldValueIsActionLevel(field)) {
 *         // TypeScript knows field.actionTypeId is string (required)
 *         console.log(`Action type: ${field.actionTypeId}`);
 *     }
 * }
 * ```
 */
export function bymoPagePropsTypedFieldValueIsActionLevel<
    TMap extends BymoPagePropsContentTypeMap
>(
    value: BymoPagePropsTypedFieldValue<TMap>
): value is BymoPagePropsTypedFieldValue.ActionLevel<TMap> {
    return !bymoPagePropsFieldLocationIsPageLevel(value.location);
}

/**
 * Validator function for AI-generated content field values.
 * Returns null if valid, or an error message describing the expected format.
 */
export type BymoPagePropsAiContentFieldValidator = (value: unknown) => string | null;

export type BymoPagePropsContentSettingMetadata<TYPE extends BymoPagePropsContentSettingType> = {
    type: TYPE;
    semanticMeaning: BymoPagePropsContentSettingSemanticMeaning.ResolveAllowedForSettingType<TYPE>;
    deprecated: boolean;
    aiDescription?: BymoPagePropsAiStringKey;
    /** Per-field validator for AI-generated values. Takes priority over type-level validators. */
    aiValidator?: BymoPagePropsAiContentFieldValidator;
}

export function bymoPagePropsResolveContentSettingsOfTypes<T extends {}> (
    contentSettingsMetadata: BymoPagePropsContentSettingsMetadata<T>,
    types: BymoPagePropsContentSettingType[]
) {
    return objectCreateFromEntries(iterableMapToArray(
        objectGetEntries(contentSettingsMetadata),
        ([settingName, settingMetadata], skip) => {
            if (!enumIsEnumValue(BymoPagePropsSpecialContentSettingMetadata, settingMetadata) && types.includes((settingMetadata as BymoPagePropsValueSupportedMetadata<any>).type)) {
                return [settingName, settingMetadata];
            }

            return skip;
        }
    ))
}

export function bymoPagePropsCreateContentSettingMetadata<TYPE extends BymoPagePropsContentSettingType>(
    type: TYPE,
    semanticMeaning: BymoPagePropsContentSettingMetadata<TYPE>['semanticMeaning'],
    options: {
        deprecated?: boolean;
        description?: BymoPagePropsAiStringKey;
        aiValidator?: BymoPagePropsAiContentFieldValidator;
    } = {}
): BymoPagePropsContentSettingMetadata<TYPE> {

    return {
        type: type,
        semanticMeaning: semanticMeaning,
        deprecated: options.deprecated ?? false,
        aiDescription: options.description,
        aiValidator: options.aiValidator,
    }
}

export function doppeListActionContentSettingsMetadataCreateForListActionWithMainDetails() {
    return {
        mainTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Text, BymoPagePropsContentSettingSemanticMeaning.EntityTitle),
        mainImage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.SingleMedia, BymoPagePropsContentSettingSemanticMeaning.EntityMedia),
    } satisfies BymoPagePropsContentSettingsMetadata<DoppeListActionSettings.WithMainDetails>
}

export function doppeListActionContentSettingsMetadataCreateForListActionCommonSettings() {
    return {
        itemShowDescription: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.itemShowDescription'}),
        itemShowImage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.itemShowImage'}),
        itemShowTitle: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.itemShowTitle'}),
        itemShowTextOnImage: bymoPagePropsCreateContentSettingMetadata(BymoPagePropsContentSettingType.Boolean, BymoPagePropsContentSettingSpecialSemanticMeaning.Unspecified, {description: 'field.itemShowTextOnImage'}),
    } satisfies BymoPagePropsContentSettingsMetadata<DoppeListActionSettings.CommonSettings>
}

export type BymoPagePropsContentSettingsFilterOptions = {
    visibleOnly: boolean;
    settingTypes: BymoPagePropsContentSettingType[];
}

export function bymoPagePropsFilterContentSettings<T extends {}> (
    settings: T,
    contentSettingsMetadata: BymoPagePropsContentSettingsMetadata<T>,
    options: BymoPagePropsContentSettingsFilterOptions
) {
    const {
        settingTypes,
        visibleOnly
    } = options;

    const filteredSettings = objectPick(
        settings,
        objectGetKeys(bymoPagePropsResolveContentSettingsOfTypes(contentSettingsMetadata, settingTypes)) as any
    );

    if (visibleOnly) {
        return objectRemoveEntries(filteredSettings, value => {
            return hideableValueIsHidden(value);
        })
    } else {
        return filteredSettings;
    }
}

export const DoppeActionTypeContentSpecialSettingsMetadata = {
    Ignored: 'Ignored'
} as const;

export type DoppeActionTypeContentSpecialSettingsMetadata = Values<typeof DoppeActionTypeContentSpecialSettingsMetadata>;